import React, { Suspense, useMemo, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { Auth0ProviderWithHistory } from './Auth0ProviderWithHistory';
import { FriendsNotificationProvider } from './contexts/FriendsNotificationContext';
import { ImportProvider } from './contexts/ImportContext';
import Layout from './components/Layout';
import ConnectionStatus from './components/ConnectionStatus';
import { CircularProgress, Box } from '@mui/material';
import { Analytics } from "@vercel/analytics/react";
import { initGA, logPageView } from './utils/analytics';

// Lazy load all page components
const VerifyEmail = React.lazy(() => import('./pages/VerifyEmail'));
const LoggedOut = React.lazy(() => import('./pages/LoggedOut'));
const SearchBooks = React.lazy(() => import('./pages/SearchBooks'));
const MyShelves = React.lazy(() => import('./pages/MyShelves'));
const Recommendations = React.lazy(() => import('./pages/Recommendations'));
const Trending = React.lazy(() => import('./pages/Trending'));
const BookDetails = React.lazy(() => import('./pages/BookDetails'));
const UserProfile = React.lazy(() => import('./pages/UserProfile'));
const Friends = React.lazy(() => import('./pages/Friends'));
const Discover = React.lazy(() => import('./pages/Discover'));
const LibraryWithTabs = React.lazy(() => import('./pages/LibraryWithTabs'));
const Onboarding = React.lazy(() => import('./components/onboarding/Onboarding'));
const Admin = React.lazy(() => import('./pages/Admin'));
const LandingPage = React.lazy(() => import('./pages/LandingPage'));
const About = React.lazy(() => import('./pages/About'));

const createAppTheme = () => createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: '#667eea',
    },
    secondary: {
      main: '#764ba2',
    },
    success: {
      main: '#4ECDC4',
    },
    warning: {
      main: '#FF8E53',
    },
    error: {
      main: '#FF6B6B',
    },
  },
  typography: {
    fontFamily: '"Comfortaa", "Roboto", "Helvetica", "Arial", sans-serif',
    h1: {
      fontFamily: '"Comfortaa", sans-serif',
      fontWeight: 700,
    },
    h2: {
      fontFamily: '"Comfortaa", sans-serif',
      fontWeight: 700,
    },
    h3: {
      fontFamily: '"Comfortaa", sans-serif',
      fontWeight: 600,
    },
    h4: {
      fontFamily: '"Comfortaa", sans-serif',
      fontWeight: 600,
    },
    h5: {
      fontFamily: '"Comfortaa", sans-serif',
      fontWeight: 600,
    },
    h6: {
      fontFamily: '"Comfortaa", sans-serif',
      fontWeight: 500,
    },
    body1: {
      fontFamily: '"Comfortaa", sans-serif',
      fontWeight: 400,
    },
    body2: {
      fontFamily: '"Comfortaa", sans-serif',
      fontWeight: 400,
    },
    button: {
      fontFamily: '"Comfortaa", sans-serif',
      fontWeight: 600,
      textTransform: 'none',
    },
  },
  shape: {
    borderRadius: 20,
  },
  components: {
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 20,
          boxShadow: '0 8px 32px 0 rgba(31, 38, 135, 0.2)',
          border: '1px solid rgba(255, 255, 255, 0.18)',
          backdropFilter: 'blur(8px)',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 16,
          fontWeight: 600,
          textTransform: 'none',
          boxShadow: '0 4px 14px 0 rgba(0, 0, 0, 0.1)',
          '&:hover': {
            boxShadow: '0 6px 20px 0 rgba(0, 0, 0, 0.15)',
            transform: 'translateY(-1px)',
          },
          transition: 'all 0.2s ease-in-out',
        },
        contained: {
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          '&:hover': {
            background: 'linear-gradient(135deg, #5a67d8 0%, #6b46c1 100%)',
          },
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 16,
            '& fieldset': {
              borderColor: 'rgba(0, 0, 0, 0.1)',
            },
            '&:hover fieldset': {
              borderColor: '#667eea',
            },
            '&.Mui-focused fieldset': {
              borderColor: '#667eea',
            },
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 20,
          fontWeight: 500,
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 20,
          boxShadow: '0 8px 32px 0 rgba(31, 38, 135, 0.15)',
        },
      },
    },
  },
});

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 10 * 60 * 1000, // 10 minutes - data stays fresh longer
      gcTime: 30 * 60 * 1000, // 30 minutes - keep in cache longer
      refetchOnWindowFocus: false,
      refetchOnMount: 'always', // Always refetch on mount (needed for cache invalidation to work)
      refetchOnReconnect: false, // Don't refetch on reconnect
      retry: 1,
    },
  },
});

function PrivateRoute({ children, skipOnboarding = false }: { children: React.ReactNode; skipOnboarding?: boolean }) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress />
      </Box>
    );
  }

  if (!user) {
    return <Navigate to="/" replace />;
  }

  // Check onboarding status unless explicitly skipping
  if (!skipOnboarding && !user.onboarding_completed) {
    return <Navigate to="/onboarding" />;
  }

  return <>{children}</>;
}

// Loading component for Suspense
const PageLoader = () => (
  <Box
    display="flex"
    justifyContent="center"
    alignItems="center"
    minHeight="60vh"
    flexDirection="column"
    gap={2}
  >
    <CircularProgress size={40} />
    <Box sx={{ fontSize: '0.9rem', color: '#757575' }}>
      Loading page...
    </Box>
  </Box>
);

function AppRoutes() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  React.useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const error = params.get('error');
    const errorDescription = params.get('error_description');

    if (error === 'access_denied' && errorDescription?.includes('verify your email')) {
      const isNewSignup = sessionStorage.getItem('isNewSignup') === 'true';
      sessionStorage.removeItem('isNewSignup');

      const signupParam = isNewSignup ? '&signup=true' : '';
      navigate(`/verify-email?error_description=${encodeURIComponent(errorDescription)}${signupParam}`, { replace: true });
    } else if (error && errorDescription) {
      // Pass through Auth0's error message directly
      navigate(`/?auth_error=${encodeURIComponent(errorDescription)}`, { replace: true });
    }
  }, [navigate]);

  React.useEffect(() => {
    logPageView(location.pathname + location.search);
  }, [location]);

  // Show loader while auth state is being determined
  if (loading) {
    return <PageLoader />;
  }

  return (
    <Suspense fallback={<PageLoader />}>
      <Routes>
        <Route path="/verify-email" element={<VerifyEmail />} />
        <Route path="/logged-out" element={<LoggedOut />} />
        <Route
          path="/onboarding"
          element={
            <PrivateRoute skipOnboarding={true}>
              <Onboarding />
            </PrivateRoute>
          }
        />
      <Route
        path="/"
        element={
          user && user.onboarding_completed ? (
            <Layout>
              <Discover />
            </Layout>
          ) : user ? (
            <Navigate to="/onboarding" replace />
          ) : (
            <LandingPage />
          )
        }
      />
      <Route
        path="/about"
        element={<About />}
      />
      <Route
        path="/search"
        element={
          <PrivateRoute>
            <Layout>
              <SearchBooks />
            </Layout>
          </PrivateRoute>
        }
      />
      <Route
        path="/library"
        element={
          <PrivateRoute>
            <Layout>
              <LibraryWithTabs />
            </Layout>
          </PrivateRoute>
        }
      />
      <Route
        path="/shelves"
        element={
          <PrivateRoute>
            <Layout>
              <MyShelves />
            </Layout>
          </PrivateRoute>
        }
      />
      <Route
        path="/recommendations"
        element={
          <PrivateRoute>
            <Layout>
              <Recommendations />
            </Layout>
          </PrivateRoute>
        }
      />
      <Route
        path="/friends"
        element={
          <PrivateRoute>
            <Layout>
              <Friends />
            </Layout>
          </PrivateRoute>
        }
      />
      <Route
        path="/trending"
        element={
          <PrivateRoute>
            <Layout>
              <Trending />
            </Layout>
          </PrivateRoute>
        }
      />
      <Route
        path="/book/:id"
        element={
          <PrivateRoute>
            <Layout>
              <BookDetails />
            </Layout>
          </PrivateRoute>
        }
      />
      <Route
        path="/book/provider/:providerBookId"
        element={
          <PrivateRoute>
            <Layout>
              <BookDetails isProviderBook={true} />
            </Layout>
          </PrivateRoute>
        }
      />
      <Route
        path="/profile/:id"
        element={
          <PrivateRoute>
            <Layout>
              <UserProfile />
            </Layout>
          </PrivateRoute>
        }
      />
      <Route
        path="/admin"
        element={
          <PrivateRoute>
            <Layout>
              <Admin />
            </Layout>
          </PrivateRoute>
        }
      />
    </Routes>
    </Suspense>
  );
}

function App() {
  const theme = useMemo(() => createAppTheme(), []);
  
  useEffect(() => {
    initGA();
  }, []);
  
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <QueryClientProvider client={queryClient}>
        <Router>
          <Auth0ProviderWithHistory>
            <AuthProvider>
              <ImportProvider>
                <FriendsNotificationProvider>
                  <AppRoutes />
                  <ConnectionStatus />
                  <Analytics />
                </FriendsNotificationProvider>
              </ImportProvider>
            </AuthProvider>
          </Auth0ProviderWithHistory>
        </Router>
      </QueryClientProvider>
    </ThemeProvider>
  );
}

export default App;